import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking' | 'valid' | 'invalid'
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }
    fetch(`${BACKEND_URL}/api/auth/reset-token-valid?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => setTokenStatus(d.valid ? 'valid' : 'invalid'))
      .catch(() => setTokenStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Contraseña actualizada');
        setDone(true);
        setTimeout(() => navigate('/'), 2500);
      } else {
        toast.error(data.detail || 'No se pudo restablecer la contraseña');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md border-2 border-black p-8" data-testid="reset-password-panel">
        <div className="text-center mb-6">
          <KeyRound className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            Restablecer contraseña
          </h1>
        </div>

        {tokenStatus === 'checking' && (
          <p className="text-center text-gray-600" data-testid="reset-checking">Comprobando enlace...</p>
        )}

        {tokenStatus === 'invalid' && !done && (
          <div className="text-center space-y-4" data-testid="reset-invalid">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="text-gray-800 font-semibold">Enlace no válido o caducado</p>
            <p className="text-sm text-gray-600">
              Los enlaces de restablecimiento son válidos durante 1 hora y sólo pueden usarse una vez.
              Solicita un enlace nuevo desde la pantalla de inicio de sesión.
            </p>
            <Link to="/">
              <Button className="w-full bg-black text-white hover:bg-gray-800">
                Volver al inicio
              </Button>
            </Link>
          </div>
        )}

        {tokenStatus === 'valid' && !done && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            <p className="text-sm text-gray-600">
              Introduce tu nueva contraseña. Debe tener al menos 6 caracteres.
            </p>
            <div>
              <Label htmlFor="new-password">Nueva contraseña</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirmar contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-black text-white hover:bg-gray-800"
              data-testid="reset-submit-btn"
            >
              {submitting ? 'Guardando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        )}

        {done && (
          <div className="text-center space-y-4" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <p className="font-semibold">¡Contraseña actualizada!</p>
            <p className="text-sm text-gray-600">Redirigiendo al inicio…</p>
          </div>
        )}
      </div>
    </div>
  );
}
